import { IconBin } from "@synthesis/shared-icons";
import { uploadFinalDecisionDocumentFile } from "@synthesis/shared-services";
import { useDelete1 as useDeleteFinalDecisionDocument } from "@synthesis/shared-services/files/final-decision-document/hooks";
import {
  useCreate as useCreateFinalDecisionDraft,
  useUpdate as useUpdateFinalDecisionDraft,
} from "@synthesis/shared-services/internal/application/hooks";
import { Button, FileIconComponent, Modal, RadioGroup, setPromiseSnack, TextArea, Tooltip } from "@synthesis/shared-ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Uploader, type UploaderListItem } from "../../../../components/uploader";
import { useUserInfo } from "../../../../shared/hooks/use-user-info";
import { invalidateInternalApplicationQueries } from "../../../../shared/utils/invalidate-application-queries";
import type { ApplicationDetailDocumentFile } from "../../application-detail-page.lib";
import {
  FINAL_DECISION_CREATE_SNACK_MESSAGES,
  FINAL_DECISION_FORM_TITLE,
  FINAL_DECISION_MODAL_TITLE,
  FINAL_DECISION_RESULT_OPTIONS,
  FINAL_DECISION_SUBMIT_LABEL,
  FINAL_DECISION_UPDATE_SNACK_MESSAGES,
  type FinalDecisionModalMode,
  type FinalDecisionValue,
} from "./final-decision.lib";

type FinalDecisionModalProps = {
  mode: FinalDecisionModalMode;
  requestId: string;
  initialDecision?: FinalDecisionValue | null;
  initialJustification?: string;
  existingDocuments?: ApplicationDetailDocumentFile[];
  onClose: () => void;
};

type FinalDecisionFormValues = {
  decision: FinalDecisionValue | "";
  justification: string;
};

const getFileKey = (file: File): string => `${file.name}:${file.size}:${file.lastModified}`;

const getDocumentFileName = (document: ApplicationDetailDocumentFile): string =>
  document.ext ? `${document.baseName}.${document.ext}` : document.baseName;

export const FinalDecisionModal = ({
  mode,
  requestId,
  initialDecision,
  initialJustification,
  existingDocuments = [],
  onClose,
}: FinalDecisionModalProps) => {
  const queryClient = useQueryClient();
  const userInfo = useUserInfo();
  const [isSaving, setIsSaving] = useState(false);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [removedDocumentIds, setRemovedDocumentIds] = useState<string[]>([]);

  const deleteDocumentMutation = useDeleteFinalDecisionDocument({ mutation: { mutationKey: ["final-decision-document-delete", requestId] } });
  const createDraftMutation = useCreateFinalDecisionDraft({ mutation: { mutationKey: ["final-decision-draft-create", requestId] } });
  const updateDraftMutation = useUpdateFinalDecisionDraft({ mutation: { mutationKey: ["final-decision-draft-update", requestId] } });

  const methods = useForm<FinalDecisionFormValues>({
    defaultValues: { decision: initialDecision ?? "", justification: initialJustification ?? "" },
    mode: "onChange",
  });

  const decision = methods.watch("decision");
  const justification = methods.watch("justification");
  const keptDocuments = existingDocuments.filter((document) => !removedDocumentIds.includes(document.id));
  const documentsCount = keptDocuments.length + newFiles.length;
  const isSubmitDisabled = isSaving || !decision || !justification.trim() || documentsCount === 0;
  // mode === "create" → POST (нет черновика); "add-version" / "edit" → PUT (черновик есть).
  const shouldCreateDraft = mode === "create";
  // На доработке документы истории нельзя удалять физически — только исключать из documentIds.
  const shouldDeleteRemovedDocuments = mode === "edit";

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleSelectFiles = (files: File[]) => {
    setNewFiles((previousFiles) => {
      const knownKeys = new Set(previousFiles.map(getFileKey));
      const appendedFiles = files.filter((file) => !knownKeys.has(getFileKey(file)));

      return [...previousFiles, ...appendedFiles];
    });
  };

  const handleDeleteFromList = (fileMeta: UploaderListItem) => {
    if (!(fileMeta instanceof File)) {
      return;
    }

    setNewFiles((previousFiles) => previousFiles.filter((file) => getFileKey(file) !== getFileKey(fileMeta)));
  };

  const handleRemoveDocument = (documentId: string) => {
    setRemovedDocumentIds((previousIds) => [...previousIds, documentId]);
  };

  const handleSubmit = methods.handleSubmit(async (formData) => {
    const decisionValue = formData.decision;

    if (!userInfo || !decisionValue) {
      return;
    }

    setIsSaving(true);

    const promise = (async () => {
      // 1. Удаляем снятые документы только при правке черновика до первой отправки.
      // При подготовке новой версии документы истории остаются в file-service.
      if (shouldDeleteRemovedDocuments) {
        for (const fileId of removedDocumentIds) {
          await deleteDocumentMutation.mutateAsync({ body: { requestId, fileId, userId: userInfo.id } });
        }
      }

      // 2. Загружаем новые файлы (бинарный upload — только runtime-обёртка, у Kubb нет хука).
      const uploadedFileIds: string[] = [];

      for (const file of newFiles) {
        const fileMeta = await uploadFinalDecisionDocumentFile({ requestId, filename: file.name, userId: userInfo.id }, file);

        if (!fileMeta.id) {
          throw new Error("API не вернул идентификатор файла");
        }

        uploadedFileIds.push(fileMeta.id);
      }

      // 3. POST создаёт черновик, PUT обновляет существующий без фиксации версии в истории.
      const body = {
        decision: decisionValue,
        justification: formData.justification.trim(),
        documentIds: [...keptDocuments.map((document) => document.id), ...uploadedFileIds],
      };

      if (shouldCreateDraft) {
        await createDraftMutation.mutateAsync({ path: { requestId }, body });
      } else {
        await updateDraftMutation.mutateAsync({ path: { requestId }, body });
      }

      // Сбрасывает и перечитывает черновик (resetQueries get2) + заявку/версии.
      await invalidateInternalApplicationQueries(queryClient, requestId);
      onClose();
    })();

    setPromiseSnack({
      promise,
      promiseSettings: {
        title: shouldCreateDraft ? FINAL_DECISION_CREATE_SNACK_MESSAGES : FINAL_DECISION_UPDATE_SNACK_MESSAGES,
      },
    });

    promise
      .catch(() => undefined)
      .finally(() => {
        setIsSaving(false);
      });
  });

  return (
    <Modal.Root isOpen setIsOpen={handleOpenChange}>
      <Modal.Content className="flex max-h-[calc(100vh-4rem)] w-[700px] flex-col overflow-hidden">
        <Modal.Title>{FINAL_DECISION_MODAL_TITLE[mode]}</Modal.Title>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-6 p-8">
              <FormProvider {...methods}>
                <h3 className="heading-h4 after:text-type-error after:ml-1 after:content-['*']">{FINAL_DECISION_FORM_TITLE[mode]}</h3>
                <fieldset>
                  <legend className="paragraph-sm text-type-common block pb-2">Результат рассмотрения</legend>
                  <RadioGroup
                    name="decision"
                    gap="md"
                    disabled={isSaving}
                    renderRadioGroupElements={FINAL_DECISION_RESULT_OPTIONS.map((option) => ({
                      id: `final-decision-${option.value}`,
                      value: option.value,
                      label: option.label,
                    }))}
                  />
                </fieldset>

                <div>
                  <TextArea
                    name="justification"
                    rows={5}
                    label="Обоснование решения"
                    placeholder="Опишите обоснование принятого решения: итоги рассмотрения, основания для выдачи технических условий или отказа."
                    disabled={isSaving}
                  />
                </div>
              </FormProvider>

              <div className="flex flex-col gap-3">
                {keptDocuments.length > 0 ? (
                  <ul className="flex list-none flex-col gap-1">
                    {keptDocuments.map((document) => (
                      <li key={document.id} className="group min-w-0 max-w-full overflow-hidden">
                        <div className="outline-system-info-70 hover:bg-neutrals-95 focus:bg-neutrals-95 group-focus-within:bg-neutrals-95 flex max-w-full items-center gap-3 overflow-x-hidden rounded p-1 transition duration-75">
                          <FileIconComponent className="size-6 shrink-0" ext={document.ext ?? ""} />
                          <div className="min-w-0 flex-1 overflow-hidden">
                            <Tooltip description={getDocumentFileName(document)}>
                              <h3 className="text-system-info-50 truncate">{getDocumentFileName(document)}</h3>
                            </Tooltip>
                          </div>
                          <Button
                            type="button"
                            className="ml-auto opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
                            theme="text"
                            size="xs"
                            disabled={isSaving}
                            rightIcon={<IconBin className="text-type-light ml-1 size-5" />}
                            onClick={() => handleRemoveDocument(document.id)}
                          >
                            Удалить
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : null}

                <Uploader multiple isViewFileList allowDeleteFromList onSelectFiles={handleSelectFiles} onDeleteFromList={handleDeleteFromList} />
              </div>
            </div>
          </div>

          <div className="flex shrink-0 gap-4 px-8 pb-6 pt-2">
            <Button type="submit" theme="primary" size="lg" disabled={isSubmitDisabled}>
              {FINAL_DECISION_SUBMIT_LABEL[mode]}
            </Button>
            <Button type="button" theme="secondary" size="lg" disabled={isSaving} onClick={() => handleOpenChange(false)}>
              Отмена
            </Button>
          </div>
        </form>
      </Modal.Content>
    </Modal.Root>
  );
};
